/* eslint-disable @typescript-eslint/no-unused-vars */
import XsollaFrame from "./lib/XsollaFrame";
import { useEffect, useRef } from "react";
import { XsollaModalTitle } from "./lib/XsollaDialogTitle";
import { DialogContent, StyledDialog } from "./styled";
import { useDialogModalRedux } from "redux/slices/dialog/hooks/useDialogModalRedux";
import { WsDialogs } from "interfaces";
import { useEnvContext, useWebStoreContext } from "contexts";
import { useAppDispatch } from "hooks";
import { offersApi } from "redux/apis";
import { useHudActions } from "components/Hud/hooks/useHudActions";
import { useBouncyChestActions } from "components/BouncyChest/hooks/useBouncyChestActions";
import { useXsollaDipHandlers } from "./hooks/useXsollaDipHandlers";
import { useXsollaMessageListener } from "./hooks/useXsollaMessageListener";
import usePopUpsV2 from "components/PopUpManagerV2/hooks/usePopUpsV2";
import { FeaturesFlags } from "helpers";
import { onCloseDIP } from "redux/slices";

const DialogXsollaPurchase = ({
	show,
	onReturnToShopClick,
	onCloseClick
}: {
	show: boolean;
	onReturnToShopClick: () => void;
	onCloseClick: () => void;
}) => {
	const { open } = usePopUpsV2();
	const dispatch = useAppDispatch();
	const { userTracker } = useWebStoreContext();
	const { isFeatureFlagEnabled } = useEnvContext();
	const isComplete = useRef(false);
	const isFailed = useRef(false);
	/* eslint-disable */
	const callBackClose = useRef<any>(null);
	/* eslint-enable */
	const { updateHudState } = useHudActions();
	const { openDialog, dialogState } = useDialogModalRedux();
	const { updateBouncyChestState } = useBouncyChestActions();
	const {
		xsollaHost: host,
		xsollaToken: token,
		postUserMetadata,
		cbClose,
		onPurchaseStarted,
		onPurchaseSuccess,
		showClose = true,
		title,
		source,
		section,
		priority,
		triggerType,
		pricePoint,
		items,
		suppressDips,
		invoice,
		rewardMultiplier
	} = dialogState;
	const loyaltyPoints = Number(invoice?.loyaltyPoints) || 0;
	const totalLoyaltyPoints = loyaltyPoints * (1 + (rewardMultiplier || 0));

	const isFirstPurchaseFtue =
		isFeatureFlagEnabled(FeaturesFlags.FTUE_V2) &&
		invoice?.isFirstPurchase === true &&
		section !== "friend-shop";
	const shouldShowOrderConfirmation =
		userTracker?.metadata?.suppressOrderConfirmation !== "true" &&
		!localStorage.getItem("store.solitairetripeaks.com:suppressConfirmation");

	const {
		handlePurchaseSuccessDips,
		handlePurchaseFailedDips,
		handlePurchaseCancelledDips
	} = useXsollaDipHandlers({
		pricePoint,
		source,
		triggerType,
		items,
		priority,
		suppressDips
	});

	const closeDialog = () => {
		cbClose && cbClose();
		onCloseClick();
	};

	const onOrderConfirmation = () => {
		if (!isComplete.current) {
			closeDialog();
			return;
		}
		if (isFirstPurchaseFtue) {
			if (source === "dips") {
				dispatch(onCloseDIP());
			}
			open({
				variant: "first-purchase",
				id: "first-purchase",
				payload: { lp: totalLoyaltyPoints }
			});
			closeDialog();
			return;
		}
		if (shouldShowOrderConfirmation) {
			openDialog(WsDialogs.SHOP_PURCHASE_SUCCESS, {
				postUserMetadata,
				cbClose
			});
			return;
		}
		closeDialog();
	};
	/* eslint-disable-next-line */
	const handleClose = (event?: any, reason?: string) => {
		if (!isComplete.current && reason && reason == "backdropClick") {
			return;
		}
		onOrderConfirmation();
	};

	const handleTitleClose = () => {
		if (
			!isComplete.current &&
			source !== "drip-offer-popup" &&
			!isFailed.current
		) {
			handlePurchaseCancelledDips();
		}
		handleClose();
	};

	useEffect(() => {
		if (show) {
			resetIsComplete();
			resetIsFailed();
		}
	}, [show]);

	useEffect(() => {
		if (cbClose) {
			callBackClose.current = cbClose;
		}
	}, [cbClose]);

	const setIsComplete = () => (isComplete.current = true);
	const resetIsComplete = () => (isComplete.current = false);
	const setIsFailed = () => (isFailed.current = true);
	const resetIsFailed = () => (isFailed.current = false);

	useXsollaMessageListener({
		source,
		onPurchaseStarted: (args) => {
			if (typeof onPurchaseStarted !== "undefined") {
				onPurchaseStarted({
					payStation: args.payStation,
					data: args.data
				});
			}
		},
		onSuccess: (data) => {
			setIsComplete();
			if (typeof onPurchaseSuccess !== "undefined") {
				onPurchaseSuccess(data);
			}
			if (!isFirstPurchaseFtue) {
				handlePurchaseSuccessDips();
			}
			if (isFeatureFlagEnabled(FeaturesFlags.HUD_LIMITED_DEALS)) {
				updateHudState();
			} else {
				updateBouncyChestState();
			}
			dispatch(offersApi.util.invalidateTags(["offersTag"]));
		},
		onFailure: () => {
			handlePurchaseFailedDips();
			setIsFailed();
		},
		onCreateInvoice: () => {
			// no-op for now; kept for potential future use
		},
		onFinish: () => {
			if (callBackClose.current) {
				callBackClose.current();
			}
			handleClose();
		}
	});

	if (!host || !token) {
		return null;
	}

	return (
		<StyledDialog
			open={show}
			onClose={handleClose}
			aria-labelledby="xsolla-dialog-title"
			sx={{
				overflow: "auto"
			}}
		>
			<XsollaModalTitle
				id="xsolla-dialog-title"
				onClose={handleTitleClose}
				isShowClose={showClose}
			>
				{title}
			</XsollaModalTitle>
			<DialogContent dividers>
				<XsollaFrame host={host} token={token} />
			</DialogContent>
		</StyledDialog>
	);
};

export { DialogXsollaPurchase };
